import React, { useState } from "react";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-transparent border-b-2 border-[#052F24]/15 focus:border-[#052F24] outline-none py-3 text-base font-light text-[#052F24] placeholder:text-[#052F24]/35 transition-colors";

export const JoinTeamModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const isValid = !!form.name.trim() && !!form.mobile.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/recruitment`, form);
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => {
        setForm({ name: "", mobile: "", email: "" });
        setDone(false);
        setError("");
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#F6F5F2] border-[#052F24]/10 rounded-2xl max-w-md" data-testid="join-team-modal">
        {done ? (
          <div className="text-center py-6" data-testid="join-team-success">
            <div className="w-14 h-14 rounded-full bg-[#052F24] flex items-center justify-center mx-auto mb-5">
              <Check className="text-[#DFFF00]" size={26} />
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight mb-2">Thanks for reaching out.</h3>
            <p className="text-[#052F24]/60 font-light text-sm">Our team will be in touch soon.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Join our team</DialogTitle>
              <DialogDescription className="text-[#052F24]/55 font-light">
                Tell us a bit about yourself and we'll get back to you.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <input className={inputCls} placeholder="Full name" value={form.name}
                onChange={(e) => set("name", e.target.value)} data-testid="join-team-name" />
              <input className={inputCls} placeholder="Mobile number" value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)} data-testid="join-team-mobile" />
              <input className={inputCls} type="email" placeholder="Email address" value={form.email}
                onChange={(e) => set("email", e.target.value)} data-testid="join-team-email" />
              {error && <p className="text-sm text-red-600" data-testid="join-team-error">{error}</p>}
              <button
                type="submit"
                disabled={!isValid || submitting}
                data-testid="join-team-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#052F24] text-[#DFFF00] font-bold py-3.5 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0a3d30] transition-colors"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : "Submit application"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
